import * as React from "react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * PixelCard — canvas-driven pixel-scatter overlay adapted from React Bits'
 * open-source `PixelCard` (https://reactbits.dev). On hover (or focus, unless
 * `noFocus`), tiny coloured squares spawn from the centre outward in a
 * delay-by-distance pattern, settle into a gentle shimmer, then disappear when
 * the pointer leaves.
 *
 * In aisquare-pretty the primary consumer is `<CardCover>`, where the pixel
 * layer overlays the cover image so the image visually recedes on hover and
 * the badges that sit on top read more clearly. Standalone usage matches the
 * upstream React Bits API so demos copy / paste cleanly.
 *
 * Two modes:
 * - **Self-managed** (default) — bind your own children inside; the wrapper
 *   listens for mouseenter / mouseleave / focus / blur and runs the
 *   appear / disappear animations.
 * - **Controlled** — pass `active={boolean}`. Useful when a parent already
 *   tracks hover (e.g. `<CardCover>` listens at the cover boundary so the
 *   pixel layer aligns with the image's hover, not the whole card's). When
 *   controlled, the wrapper does NOT bind its own pointer / focus handlers.
 *
 * Honours `prefers-reduced-motion`: the hover delay-by-distance staircase is
 * collapsed to zero and pixel speed throttles to a near-static reveal so
 * users who opt out still see the colour shift without the strobe.
 *
 * SSR-safe: all `window` access is guarded inside `useEffect`; the canvas
 * draws nothing until mounted client-side.
 */

class Pixel {
  width: number;
  height: number;
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  color: string;
  speed: number;
  size: number;
  sizeStep: number;
  minSize: number;
  maxSizeInteger: number;
  maxSize: number;
  delay: number;
  counter: number;
  counterStep: number;
  isIdle: boolean;
  isReverse: boolean;
  isShimmer: boolean;

  constructor(
    canvas: HTMLCanvasElement,
    context: CanvasRenderingContext2D,
    x: number,
    y: number,
    color: string,
    speed: number,
    delay: number,
  ) {
    this.width = canvas.width;
    this.height = canvas.height;
    this.ctx = context;
    this.x = x;
    this.y = y;
    this.color = color;
    this.speed = this.getRandomValue(0.1, 0.9) * speed;
    this.size = 0;
    this.sizeStep = Math.random() * 0.4;
    this.minSize = 0.5;
    this.maxSizeInteger = 2;
    this.maxSize = this.getRandomValue(this.minSize, this.maxSizeInteger);
    this.delay = delay;
    this.counter = 0;
    this.counterStep = Math.random() * 4 + (this.width + this.height) * 0.01;
    this.isIdle = false;
    this.isReverse = false;
    this.isShimmer = false;
  }

  getRandomValue(min: number, max: number) {
    return Math.random() * (max - min) + min;
  }

  draw() {
    const centerOffset = this.maxSizeInteger * 0.5 - this.size * 0.5;
    this.ctx.fillStyle = this.color;
    this.ctx.fillRect(this.x + centerOffset, this.y + centerOffset, this.size, this.size);
  }

  appear() {
    this.isIdle = false;
    if (this.counter <= this.delay) {
      this.counter += this.counterStep;
      return;
    }
    if (this.size >= this.maxSize) {
      this.isShimmer = true;
    }
    if (this.isShimmer) {
      this.shimmer();
    } else {
      this.size += this.sizeStep;
    }
    this.draw();
  }

  disappear() {
    this.isShimmer = false;
    this.counter = 0;
    if (this.size <= 0) {
      this.isIdle = true;
      return;
    }
    this.size -= 0.1;
    this.draw();
  }

  shimmer() {
    if (this.size >= this.maxSize) {
      this.isReverse = true;
    } else if (this.size <= this.minSize) {
      this.isReverse = false;
    }
    if (this.isReverse) {
      this.size -= this.speed;
    } else {
      this.size += this.speed;
    }
  }
}

const getEffectiveSpeed = (value: number, reducedMotion: boolean) => {
  const max = 100;
  const throttle = 0.001;
  if (value <= 0 || reducedMotion) return 0;
  if (value >= max) return max * throttle;
  return value * throttle;
};

type PixelAnimationName = "appear" | "disappear";

type PixelVariant = "default" | "blue" | "yellow" | "pink" | "muted";

interface VariantConfig {
  gap: number;
  speed: number;
  colors: string;
  noFocus: boolean;
}

const VARIANTS: Record<PixelVariant, VariantConfig> = {
  default: { gap: 5, speed: 35, colors: "#f8fafc,#f1f5f9,#cbd5e1", noFocus: false },
  blue: { gap: 10, speed: 25, colors: "#e0f2fe,#7dd3fc,#0ea5e9", noFocus: false },
  yellow: { gap: 3, speed: 20, colors: "#fef08a,#fde047,#eab308", noFocus: false },
  pink: { gap: 6, speed: 80, colors: "#fecdd3,#fda4af,#e11d48", noFocus: true },
  // `muted` is pretty's own variant — denser, neutral pixels tuned for
  // overlaying photographic covers. Smaller `gap` packs more pixels onto the
  // image so a packed photo recedes meaningfully on hover, letting the badge
  // strip read clearly. Slightly faster `speed` so the overlay reaches max
  // density before the user's eye fixes on a chip.
  muted: { gap: 4, speed: 60, colors: "#f8fafc,#e2e8f0,#94a3b8", noFocus: false },
};

export interface PixelCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /**
   * Preset bundle. Selects gap + speed + colours together. Override any
   * single dimension by passing the matching prop explicitly.
   */
  variant?: PixelVariant;
  /** Pixel grid spacing (px). Smaller = denser canvas. */
  gap?: number;
  /** Animation speed scalar. Higher = faster appear / shimmer. */
  speed?: number;
  /** Comma-separated CSS colour list — pixels pick one at random per cell. */
  colors?: string;
  /** Skip focus / blur handlers (mouse-only). */
  noFocus?: boolean;
  /**
   * Controlled mode. When set to a boolean, the wrapper stops binding its own
   * pointer / focus handlers and animates strictly in response to this prop —
   * `true` runs `appear`, `false` runs `disappear`. Leave undefined for the
   * upstream React Bits self-managed behaviour.
   */
  active?: boolean;
  /**
   * If true, the canvas overlay is positioned `absolute inset-0` and the
   * wrapper itself becomes a no-op (no fixed size, no border). Use this when
   * the overlay must paint on top of an existing visual without changing its
   * box. Default `false` keeps the upstream demo styling (300x400 card).
   */
  overlay?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const PixelCard = React.forwardRef<HTMLDivElement, PixelCardProps>(
  (
    {
      variant = "default",
      gap,
      speed,
      colors,
      noFocus,
      active,
      overlay = false,
      className,
      children,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      tabIndex,
      ...rest
    },
    forwardedRef,
  ) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [forwardedRef],
    );
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const pixelsRef = React.useRef<Pixel[]>([]);
    const animationRef = React.useRef<number | null>(null);
    const timePreviousRef = React.useRef<number>(0);

    const reducedMotionState = useReducedMotion();
    const reducedMotion = reducedMotionState === true;

    const cfg = VARIANTS[variant] ?? VARIANTS.default;
    const finalGap = gap ?? cfg.gap;
    const finalSpeed = speed ?? cfg.speed;
    const finalColors = colors ?? cfg.colors;
    const finalNoFocus = noFocus ?? cfg.noFocus;
    const isControlled = typeof active === "boolean";

    const initPixels = React.useCallback(() => {
      if (typeof window === "undefined") return;
      if (!containerRef.current || !canvasRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      if (width === 0 || height === 0) {
        pixelsRef.current = [];
        return;
      }
      const ctx = canvasRef.current.getContext("2d");
      if (!ctx) return;

      canvasRef.current.width = width;
      canvasRef.current.height = height;
      canvasRef.current.style.width = `${width}px`;
      canvasRef.current.style.height = `${height}px`;

      const colorsArray = finalColors.split(",").map((c) => c.trim()).filter(Boolean);
      const step = Math.max(1, Math.floor(Number(finalGap) || 1));
      const speedValue = getEffectiveSpeed(finalSpeed, reducedMotion);
      const pxs: Pixel[] = [];
      for (let x = 0; x < width; x += step) {
        for (let y = 0; y < height; y += step) {
          const color = colorsArray[Math.floor(Math.random() * colorsArray.length)] ?? "#cbd5e1";
          const dx = x - width / 2;
          const dy = y - height / 2;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const delay = reducedMotion ? 0 : distance;
          pxs.push(new Pixel(canvasRef.current, ctx, x, y, color, speedValue, delay));
        }
      }
      pixelsRef.current = pxs;
    }, [finalColors, finalGap, finalSpeed, reducedMotion]);

    const doAnimate = React.useCallback((fnName: PixelAnimationName) => {
      animationRef.current = requestAnimationFrame(() => doAnimate(fnName));
      const timeNow = performance.now();
      const timePassed = timeNow - timePreviousRef.current;
      const timeInterval = 1000 / 60;
      if (timePassed < timeInterval) return;
      timePreviousRef.current = timeNow - (timePassed % timeInterval);

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let allIdle = true;
      const list = pixelsRef.current;
      for (let i = 0; i < list.length; i++) {
        const pixel = list[i];
        pixel[fnName]();
        if (!pixel.isIdle) allIdle = false;
      }
      if (allIdle && animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    }, []);

    const handleAnimation = React.useCallback(
      (name: PixelAnimationName) => {
        if (animationRef.current !== null) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        timePreviousRef.current = typeof performance !== "undefined" ? performance.now() : 0;
        animationRef.current = requestAnimationFrame(() => doAnimate(name));
      },
      [doAnimate],
    );

    // Init + re-init on size / config change.
    React.useEffect(() => {
      initPixels();
      if (typeof window === "undefined" || !containerRef.current) return;
      const observer = new ResizeObserver(() => initPixels());
      observer.observe(containerRef.current);
      return () => {
        observer.disconnect();
        if (animationRef.current !== null) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
      };
    }, [initPixels]);

    // Controlled-mode driver.
    React.useEffect(() => {
      if (!isControlled) return;
      handleAnimation(active ? "appear" : "disappear");
    }, [active, handleAnimation, isControlled]);

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseEnter?.(e);
      if (!isControlled) handleAnimation("appear");
    };
    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
      onMouseLeave?.(e);
      if (!isControlled) handleAnimation("disappear");
    };
    const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
      onFocus?.(e);
      if (isControlled || finalNoFocus) return;
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      handleAnimation("appear");
    };
    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
      onBlur?.(e);
      if (isControlled || finalNoFocus) return;
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      handleAnimation("disappear");
    };

    return (
      <div
        ref={setRefs}
        className={cn(
          // Overlay mode: fill the parent. Self-managed mode: ship the upstream
          // demo card box so docs / playground demos render at a sensible size
          // without the consumer having to set width/height.
          overlay
            ? "absolute inset-0 pointer-events-none"
            : [
                "relative grid place-items-center isolate select-none overflow-hidden",
                "h-[400px] w-[300px] aspect-[4/5]",
                "rounded-2xl border border-border/70",
                "transition-colors duration-200 ease-[cubic-bezier(0.5,1,0.89,1)]",
              ].join(" "),
          className,
        )}
        onMouseEnter={isControlled ? onMouseEnter : handleMouseEnter}
        onMouseLeave={isControlled ? onMouseLeave : handleMouseLeave}
        onFocus={isControlled || finalNoFocus ? onFocus : handleFocus}
        onBlur={isControlled || finalNoFocus ? onBlur : handleBlur}
        tabIndex={isControlled ? tabIndex : finalNoFocus ? (tabIndex ?? -1) : (tabIndex ?? 0)}
        {...rest}
      >
        <canvas ref={canvasRef} className="block h-full w-full" aria-hidden />
        {children}
      </div>
    );
  },
);
PixelCard.displayName = "PixelCard";
